import styled from "styled-components";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import {
  StyledActionPreview,
  StyledBookingsDivider,
  StyledFlagInformation,
} from "../styles";
import Button from "@/common/elements/Button";
import InmateFlag, { INMATE_FLAG_TYPE } from "@/components/InmateFlag";
import Alert from "@/common/elements/Alerts";
import CustomModal from "@/common/elements/Modal";
import { useState, useEffect, Suspense } from "react";
import FlagInformationDetails from "./FlagInformationDetails";
import FolderBioView from "./FolderBioView";
import PersonalInformationDetails from "../PersonalInformationDetails";
import BookingAdministrativeInformation from "../BookingAdministrativeInformation";
import FolderDataTable from "@/components/FolderDetailSearch/FolderDataTable";
import { useDispatch, useSelector } from "react-redux";
import { PHOTOS_BASE_URL } from "@/util/api";
import { Flex, Image, Spin } from "antd";
import defaultAvatar from "@/assets/default_avatar.svg";
import { isNonEmptyVariable } from "@/util/common_function";
import notificationService from "@/util/notificationService";
import {
  getRelevantInvolvements,
  getAllInvolvements,
  getDisplayInvolvements,
} from "./flagUtils";
import { fetchFolderDetails } from "./folderDetailsApi";
import dayjs from "dayjs";
import Table from "@/common/elements/Table";
import { TableHeader } from "@/components/InmateDetails/styles";
import BookingsPage from "@/pages/Bookings";
import Modal from "@/common/elements/Modal";
import BookingsDetails from "..";
import Staffing from "@/pages/Staffing";
import {
  setBookingsFieldsEnabled,
  setShowBookings,
} from "@/slices/bookingsSlice";

const StyledFolderDetails = styled.div`
  .profile-link {
    text-decoration: underline;
    font-size: 16px;
    padding: 0;
    background: transparent !important;
    color: var(--text-accent);
    &:hover {
      color: var(--text-accent-hover);
      background: transparent !important;
    }
  }
`;

const FOLDER_BOOKING_HEADERS = [
  { key: "bookingNumber", text: "Booking Number" },
  { key: "incarcerationDate", text: "Incarceration Date" },
  { key: "releaseDate", text: "Release Date" },
  { key: "housingAgency", text: "Housing Agency" },
  { key: "housingLocation", text: "Current Location" },
];

// Jackets.MasterTransactionDate comes back either as a LocalDateTime array
// ([y, m, d, h, min, s?]) or as a string; shown the way ASP's jacket view
// does ("7/14/2026 2:50:00 PM").
const formatMasterTransactionDate = (value) => {
  if (!value) return "-";
  const parsed = Array.isArray(value)
    ? dayjs(new Date(value[0], value[1] - 1, value[2], value[3] || 0, value[4] || 0, value[5] || 0))
    : dayjs(value);
  return parsed.isValid() ? parsed.format("M/D/YYYY h:mm:ss A") : String(value);
};

const FolderFullDetails = ({
  showFoldersList = true,
  onFolderSelected,
  selectedFolderTemp,
  associates,
  parentKey,
  loadFromFolder = false,
  // Folder to load when the host isn't the open Booking (e.g. a Civil Papers
  // case association) - otherwise it's the open booking's own jacketId.
  jacketId: jacketIdProp = null,
}) => {
  // Grab bookings from redux.
  const reduxBookings = useSelector((state) => state?.bookings?.bookings);
  const jacketFlags = useSelector((state) => state?.bookings?.jacketFlags);
  const jacketId = jacketIdProp ?? reduxBookings?.jacketId;
  // loadFromFolder: the header folder icon's modal - show the folder's own
  // bio/flags/bookings (fetched by jacketId) instead of the open booking's
  // copy, like ASP's Jacket Details dialog. A brand-new booking with no
  // jacketId yet has no folder to load, so it keeps using the booking data.
  const useFolderData = loadFromFolder && !associates && !!jacketId;
  const [folderData, setFolderData] = useState(null);
  const [folderLoading, setFolderLoading] = useState(useFolderData);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [bioPhotoUrl, setBioPhotoUrl] = useState("");
  const [bioPhotos, setBioPhotos] = useState([]);
  const [bioName, setBioName] = useState("New Folder");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showBioPhotoIndex, setShowBioPhotoIndex] = useState(-1);
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();

  const [header, setHeaders] = useState([
    { key: "bookingId", text: "Booking ID" },
    { key: "dateAdded", text: "Date Created" },
    { key: "timeAdded", text: "Time Created" },
    { key: "arrestDate", text: "Arrest Date" },
    { key: "releaseDate", text: "Release Date" },
    { key: "housed", text: "Housed" },
    { key: "caseNumber", text: "Case Number" },
    { key: "offenseDate", text: "Offense Date" },
  ]);
  useEffect(() => {
    if (parentKey == "staff") {
      setHeaders([
        { key: "staffId", text: "ID" },
        { key: "dateAdded", text: "Date Created" },
        { key: "rank", text: "Rank" },
        { key: "division", text: "Division" },
        { key: "badge", text: "Badge" },
      ]);
    }
  }, [parentKey]);
  const bookingsArray = Array.isArray(reduxBookings)
    ? reduxBookings
    : [reduxBookings];

  const handleInfoModalVisible = () => {
    setShowInfoModal(!showInfoModal);
  };

  useEffect(() => {
    if (associates) {
      setSelectedFolder(selectedFolderTemp);
    }
  }, [selectedFolderTemp]);

  // When a row in the FolderDataTable is clicked,
  // update local state and inform the parent if needed.
  const handleRowClick = (rowData) => {
    setSelectedFolder(rowData);
    if (onFolderSelected) {
      onFolderSelected(rowData);
    }
  };

  useEffect(() => {
    if (!useFolderData) {
      setFolderData(null);
      setFolderLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFolderLoading(true);
    fetchFolderDetails(jacketId).then((data) => {
      if (cancelled) return;
      if (!data.bio) {
        notificationService.error(
          "Unable to load folder details",
          "Showing the current booking's information instead.",
        );
      }
      setFolderData(data);
      setFolderLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [useFolderData, jacketId]);

  // Every booking in the folder (ASP's Jackets\Bookings), as returned by
  // bookings/search - so the columns are that endpoint's fields, the same
  // ones Bookings' own ReportList shows. Staff keeps its existing list.
  const showFolderBookings =
    useFolderData &&
    parentKey != "staff" &&
    Array.isArray(folderData?.bookings);

  const handleRecordRowClick = (row) => {
    // BookingsPage (fromFolderDetails) renders the booking already in redux,
    // so only the open booking's own row can be shown from here without
    // replacing the booking being edited.
    if (
      showFolderBookings &&
      String(row?.bookingNumber) !== String(reduxBookings?.bookingNumber)
    ) {
      notificationService.info(
        `Booking #${row?.bookingNumber} is not the open booking`,
        "Open it from the Bookings module to view its details.",
      );
      return;
    }
    setShowModal(true);
  };

  // Folder's own bio when it loaded; otherwise the open booking's copy.
  const currentBio = associates
    ? selectedFolder?.originalData?.jackets?.bio?.[0]
    : (folderData?.bio ?? reduxBookings?.bio?.[0]);

  useEffect(() => {
    const bioData = currentBio;

    if (bioData) {
      // Full name, same order as the InmateHeader: "First Middle Last, Suffix".
      const fullName = [bioData.fName, bioData.mName, bioData.lName]
        .filter((part) => isNonEmptyVariable(part) && String(part).trim())
        .join(" ");
      setBioName(
        fullName
          ? `${fullName}${bioData.suffix ? `, ${bioData.suffix}` : ""}`
          : "New Folder",
      );
      const photos = bioData.bioPhotos || [];
      setBioPhotos(photos);
      if (
        photos.length > 0 &&
        photos[0].photos &&
        photos[0].photos[0]?.fullSizePath
      ) {
        setBioPhotoUrl(
          PHOTOS_BASE_URL + getFinalPath(photos[0].photos[0].fullSizePath),
        );
        setShowBioPhotoIndex(0);
      } else {
        setBioPhotoUrl(defaultAvatar);
      }
    } else {
      setBioName("New Folder");
      setBioPhotos([]);
      setBioPhotoUrl(defaultAvatar);
      setShowBioPhotoIndex(-1);
    }
  }, [currentBio]);

  const getFinalPath = (path) => {
    if (path?.includes("\\")) {
      const splitPath = path.split("/");
      if (splitPath.length === 4) {
        splitPath.shift();
        splitPath.shift();
      }
      return splitPath.join("\\");
    }
    return path;
  };

  const handleImageError = () => {
    setBioPhotoUrl(defaultAvatar);
  };

  const prevBioPhoto = () => {
    if (showBioPhotoIndex > 0) {
      setShowBioPhotoIndex(showBioPhotoIndex - 1);
    }
  };

  const nextBioPhoto = () => {
    if (bioPhotos && showBioPhotoIndex < bioPhotos.length - 1) {
      setShowBioPhotoIndex(showBioPhotoIndex + 1);
    }
  };

  useEffect(() => {
    if (bioPhotos.length > 0 && showBioPhotoIndex !== -1) {
      const currentPhoto = bioPhotos[showBioPhotoIndex];
      if (
        currentPhoto &&
        currentPhoto.photos &&
        currentPhoto.photos[0]?.fullSizePath
      ) {
        setBioPhotoUrl(
          PHOTOS_BASE_URL + getFinalPath(currentPhoto.photos[0].fullSizePath),
        );
      } else {
        setBioPhotoUrl(defaultAvatar);
      }
    }
  }, [bioPhotos, showBioPhotoIndex]);

  const renderFlag = (flagType) => {
    // Includes each Classification questionnaire's own nested cdInvolvement,
    // not just the root cdInvolvements array - see getAllInvolvements. Same
    // fix as InmateHeader's renderFlag, so the "Folder Details" modal's flag
    // badges stay consistent with the header's.
    const cdInvolvements = associates
      ? getAllInvolvements(selectedFolder?.originalData)
      : (folderData?.cdInvolvements ??
        getDisplayInvolvements(reduxBookings, jacketFlags));
    const relevantInvolvements = getRelevantInvolvements(
      cdInvolvements,
      flagType,
    );
    const flagEnabled = relevantInvolvements.length > 0;
    return (
      <InmateFlag
        key={flagType}
        type={flagType}
        flagEnabled={flagEnabled}
        cdInvolvements={relevantInvolvements}
      />
    );
  };

  const renderFlagBar = () => (
    <StyledFlex justify="start" style={{ position: "relative" }}>
      {renderFlag(INMATE_FLAG_TYPE.WARRANT)}
      {renderFlag(INMATE_FLAG_TYPE.CIVIC)}
      {renderFlag(INMATE_FLAG_TYPE.DANGER)}
      {renderFlag(INMATE_FLAG_TYPE.INJAIL)}
      {renderFlag(INMATE_FLAG_TYPE.GANG)}
      {renderFlag(INMATE_FLAG_TYPE.MEDICAL)}
      {renderFlag(INMATE_FLAG_TYPE.NOTES)}
    </StyledFlex>
  );

  const renderPhotoPreview = () => (
    <StyledActionPreview>
      <Image
        src={bioPhotoUrl}
        alt="Default Inmate Avatar"
        width="100%"
        onError={handleImageError}
        fallback={defaultAvatar}
      />
      <div className="action-pagination">
        <Button
          type="text"
          color="default"
          variant="text"
          onClick={prevBioPhoto}
        >
          <CaretLeftOutlined />
        </Button>
        <Button
          type="text"
          color="default"
          variant="text"
          onClick={nextBioPhoto}
        >
          <CaretRightOutlined />
        </Button>
        {bioPhotos.length ? (
          <div className="pages">
            {showBioPhotoIndex + 1}/{bioPhotos.length}
          </div>
        ) : (
          ""
        )}
      </div>
      <Flex justify="space-around">
        <p>
          {bioPhotos?.[showBioPhotoIndex]?.photos?.[0]?.dateAdded ||
            ""}{" "}
        </p>
        <p>
          {bioPhotos?.[showBioPhotoIndex]?.photos?.[0]?.timeAdded ||
            ""}{" "}
        </p>
      </Flex>
      <Flex justify="center">
        <p>
          {bioPhotos?.[showBioPhotoIndex]?.photos?.[0]?.imageType ||
            ""}{" "}
        </p>
      </Flex>
    </StyledActionPreview>
  );

  // Folder's own value when it loaded; otherwise the jacket the open booking
  // was loaded with.
  const masterTransactionDate =
    folderData?.masterTransactionDate ??
    reduxBookings?.jackets?.[0]?.masterTransactionDate;

  return (
    <Spin spinning={folderLoading}>
      {(selectedFolder || !associates) && (
        <StyledFolderDetails>
          <StyledGridRow gutter={16}>
            <StyledGridCol xs={6}>
              {renderPhotoPreview()}
            </StyledGridCol>
            <StyledGridCol xs={18}>
              <StyledGridRow gutter={16} justify="space-between" align="middle">
                <StyledGridCol>
                  {isNonEmptyVariable(associates) ? (
                    <p className="text-dark fw-bold">{bioName}</p>
                  ) : (
                    <Button
                      type="text"
                      variant="text"
                      className="profile-link"
                      onClick={handleInfoModalVisible}
                    >
                      {bioName}
                    </Button>
                  )}
                </StyledGridCol>
                <StyledGridCol style={{ padding: "0px 30px" }}>
                  {renderFlagBar()}
                </StyledGridCol>
                <StyledGridCol lg={24}>
                  <StyledBookingsDivider
                    style={{ marginBottom: "15px", marginTop: "15px" }}
                  />
                </StyledGridCol>
              </StyledGridRow>
              <FlagInformationDetails
                flagDetailsSource={currentBio}
                associates={associates || useFolderData}
              />
              <Alert
                type="info"
                show={true}
                style={{ marginTop: "10px", marginBottom: "25px" }}
                message={`The current record is in the folder named ${bioName}. If you want to move this record to a different folder or create a new folder, 
                click the 'Search for folder' button. You should make every effort to identify an existing folder for this person and put this record into that folder.`}
              />
            </StyledGridCol>
          </StyledGridRow>
        </StyledFolderDetails>
      )}
      {showFoldersList && <FolderDataTable onRowClick={handleRowClick} />}
      {showInfoModal && (
        <CustomModal
          header={bioName ? `${bioName} Information` : "New Folder"}
          show={showInfoModal}
          size="xl"
          onHide={handleInfoModalVisible}
          // footerActions={[{ key: "save", text: "Save" }]}
          footerCloseAction
          footerCloseActionText="Close"
          footerActionsNeed={false}
        >
          {/* Top of ASP's JacketsInterface.asp: the jacket's flag bar, then
              "Most Recent Record Added to the Jacket" (MasterTransactionDate). */}
          <StyledFlex justify="space-between" align="center" gap="20px" wrap="wrap">
            <StyledFlagInformation>
              <label className="info-label">
                Most Recent Record Added to the Folder
              </label>
              <div className="info-value">
                {formatMasterTransactionDate(masterTransactionDate)}
              </div>
            </StyledFlagInformation>
            {renderFlagBar()}
          </StyledFlex>
          <StyledSectionHeading>
            <span>Personal Information</span>
          </StyledSectionHeading>
          {useFolderData ? (
            // Read-only folder bio, laid out like ASP's Jacket Details -
            // PersonalInformationDetails is the open booking's editable form,
            // bound to redux.
            <FolderBioView
              bio={currentBio}
              photo={renderPhotoPreview()}
              isStaff={parentKey == "staff"}
            />
          ) : (
            <PersonalInformationDetails />
          )}
          <StyledSectionHeading>
            <span>{parentKey == "staff" ? "Staff" : "Bookings"} Records</span>
          </StyledSectionHeading>
          <Table
            headers={showFolderBookings ? FOLDER_BOOKING_HEADERS : header || []}
            data={
              showFolderBookings
                ? folderData.bookings
                : parentKey == "staff"
                  ? bookingsArray.map((booking) => ({
                      staffId: booking?.staffId,
                      dateAdded: booking?.dateAdded,
                      rank: booking?.rankHr[0]?.rank,
                      division: booking?.organization[0]?.division,
                      badge: booking?.badge,
                    }))
                  : bookingsArray.map((booking) => ({
                      bookingId: booking?.bookingId || booking?.staffId,
                      dateAdded: booking?.dateAdded,
                      timeAdded: booking?.timeAdded,
                      arrestDate: booking?.arrestDate,
                      releaseDate: booking?.releaseDate,
                      housed: booking?.housed,
                      caseNumber: booking?.caseNumber,
                      offenseDate: booking?.offenseDate,
                    }))
            }
            onRowClick={handleRecordRowClick}
          />
          <TableHeader>
            <StyledFlex
              gap="10px"
              style={{
                padding: "5px",
              }}
            >
              <Button>Details</Button>
              <Button>Config</Button>
            </StyledFlex>
          </TableHeader>
          <StyledSectionHeading>
            <span>Administrative Information</span>
          </StyledSectionHeading>
          <BookingAdministrativeInformation />
        </CustomModal>
      )}
      {showModal && (
        <>
          {parentKey == "staff" ? (
            <Modal
              designPattern="ant"
              header={"Staff"}
              show={showModal}
              onHide={() => setShowModal(false)}
              size="xxl"
              backdrop="static"
              footerActionsNeed={false}
              maskClosable={false}
              rootClassName="custom-modal-mask-dark"
              // footerCloseAction
              // footerCloseActionText="Cancel"
              width={"95%"}
            >
              <Suspense
                fallback={<p>Loading...</p>}
                style={{ minHeight: "300px" }}
              >
                <Staffing fromFolderDetails={true} />
              </Suspense>
            </Modal>
          ) : (
            <Modal
              designPattern="ant"
              header={"Bookings"}
              show={showModal}
              onHide={() => setShowModal(false)}
              size="xxl"
              backdrop="static"
              footerActionsNeed={false}
              maskClosable={false}
              rootClassName="custom-modal-mask-dark"
              // footerCloseAction
              // footerCloseActionText="Cancel"
              width={"95%"}
            >
              <Suspense
                fallback={<p>Loading...</p>}
                style={{ minHeight: "300px" }}
              >
                <BookingsPage
                  isBQ={true}
                  nextBookingStep={null}
                  bqCompleteSaveAction={() => {}}
                  fromBookingStep={true}
                  fromFolderDetails={true}
                />
              </Suspense>
            </Modal>
          )}
        </>
      )}
    </Spin>
  );
};

export default FolderFullDetails;
